import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { alertsAPI } from '../../services/api';
import AuthModal from '../auth/AuthModal';
import Tooltip from '../common/Tooltip';

/**
 * Bell icon toggle for scheme, collection, or funder alerts.
 *
 * Props:
 *   schemeId        — scheme PK (for scheme-level alerts)
 *   collectionId    — collection PK (for collection-level alerts)
 *   funderId        — organisation PK (for funder-level alerts)
 *   variant         — "light" for dark backgrounds (FundingDetail header)
 *   initialActive   — if provided, skips the check API call and uses this value
 *
 * Pass exactly one of schemeId, collectionId, or funderId.
 */
const AlertButton = ({ schemeId, collectionId, funderId, variant, initialActive, onToggle }) => {
  const { isAuthenticated } = useAuth();
  const [active, setActive] = useState(initialActive ?? false);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(initialActive != null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const alertType = schemeId != null ? 'scheme' : collectionId != null ? 'collection' : 'funder';
  const id = schemeId ?? collectionId ?? funderId;

  const checkFn = { scheme: alertsAPI.checkScheme, collection: alertsAPI.checkCollection, funder: alertsAPI.checkFunder };
  const toggleFn = { scheme: alertsAPI.toggleScheme, collection: alertsAPI.toggleCollection, funder: alertsAPI.toggleFunder };

  // Check alert status on mount (skipped when initialActive is provided)
  useEffect(() => {
    if (!isAuthenticated || !id || checked) return;
    let cancelled = false;

    (async () => {
      try {
        const data = await checkFn[alertType](id);
        if (!cancelled) {
          setActive(data.active);
          setChecked(true);
        }
      } catch {
        // silently ignore — button defaults to inactive
      }
    })();

    return () => { cancelled = true; };
  }, [isAuthenticated, id, alertType, checked]);

  const handleClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    setLoading(true);
    try {
      const data = await toggleFn[alertType](id);
      setActive(data.active);
      if (typeof onToggle === 'function') onToggle(data.active);
    } catch (err) {
      console.error('Error toggling alert:', err);
    } finally {
      setLoading(false);
    }
  };

  const isLight = variant === 'light';
  const tooltipText = active ? 'Remove alert' : 'Alert me when status changes';

  const buttonClass = isLight
    ? `p-1.5 rounded transition-colors ${loading ? 'opacity-50 cursor-not-allowed' : ''}`
    : `p-1.5 rounded transition-colors ${
        active ? 'text-yellow-500 hover:text-yellow-600' : 'text-gray-400 hover:text-gray-700'
      } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`;

  return (
    <>
      <Tooltip text={tooltipText}>
        <button
          onClick={handleClick}
          disabled={loading}
          className={buttonClass}
          style={isLight ? { color: active ? '#facc15' : 'rgba(255,255,255,0.75)' } : undefined}
        >
          <svg
            className="h-5 w-5"
            fill={active ? 'currentColor' : 'none'}
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
            />
          </svg>
        </button>
      </Tooltip>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        action="set alerts on funding opportunities"
      />
    </>
  );
};

export default AlertButton;
